<template>
  <div class="items-center justify-between dark:bg-dark-600 bg-primary-10">
    <ul class="w-full h-full flex flex-col">
      <li>
        <SideMenuButton
          :icon="'pie_chart'"
          :isDisabled="leftSideFrame == 'Statistics'"
          :text="$t('message.statistics')"
          @click="setLeftSideFrame('Statistics')"
        />
      </li>
      <li v-if="displayLanguage == 'Japanese' && get_topicId(problemStatePresenter.problemId.value) == 'INFO1'">
        <SideMenuButton
          :icon="'import_contacts'"
          :isDisabled="leftSideFrame == 'TopicCommentary'"
          :text="$t('message.topicCommentary')"
          @click="setLeftSideFrame('TopicCommentary')"
        />
      </li>
      <li v-if="preList.length > 0">
        <SideMenuButton
          :icon="'live_help'"
          :isDisabled="leftSideFrame == 'PreCommentary'"
          :text="$t('message.preCommentary')"
          @click="setLeftSideFrame('PreCommentary')"
        />
      </li>
      <li>
        <SideMenuButton
          :icon="'description'"
          :isDisabled="leftSideFrame == 'Problem'"
          :text="$t('message.problemDescription')"
          @click="setLeftSideFrame('Problem')"
        />
      </li>
      <li v-if="postList.length > 0">
        <SideMenuButton
          :icon="'contact_support'"
          :isDisabled="leftSideFrame == 'PostCommentary'"
          :text="$t('message.postCommentary')"
          @click="setLeftSideFrame('PostCommentary')"
        />
      </li>
      <li v-if="isLogin">
        <SideMenuButton
          :icon="'history'"
          :isDisabled="leftSideFrame == 'History'"
          :text="$t('message.submissionHistory')"
          @click="setLeftSideFrame('History')"
        />
      </li>
      <li>
        <SideMenuButton
          :icon="'group'"
          :isDisabled="leftSideFrame == 'ModelAnswer'"
          :text="$t('message.modelAnswer')"
          @click="setLeftSideFrame('ModelAnswer')"
        />
      </li>
      <li v-if="isLogin">
        <SideMenuButton
          id="review"
          :icon="'reviews'"
          :isDisabled="leftSideFrame == 'Review'"
          :text="$t('message.review')"
          @click="setLeftSideFrame('Review')"
        />
      </li>
      <li v-if="arenaPresenter.arenaId.value != '' && arenaPresenter.arenaProblemIndex.value > 0" class="mt-auto">
        <SideMenuButton :icon="'arrow_back'" :text="$t('message.prevProblem')" @click="() => setPrevArenaProblem()" />
      </li>
      <li
        v-else-if="arenaPresenter.arenaId.value == '' && problemStatePresenter.prevProblemId.value != '-1'"
        class="mt-auto"
      >
        <SideMenuButton
          :icon="'arrow_back'"
          :text="$t('message.prevProblem')"
          @click="() => problemStatePresenter.setProblemId(problemStatePresenter.prevProblemId.value)"
        />
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'

import SideMenuButton from './children/SideMenuButton.vue'

import { useArenaPresenter } from '@/presentation/composables/arena/useArenaPresenter'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useFrameStatePresenter } from '@/presentation/composables/frame/useFrameStatePresenter'
import { useProblemDescriptionPresenter } from '@/presentation/composables/problem/useProblemDescriptionPresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'

export default defineComponent({
  components: {
    SideMenuButton
  },
  setup(props, ctx) {
    const { isLogin } = useAuthStatePresenter()
    const { leftSideFrame, setLeftSideFrame } = useFrameStatePresenter()
    const problemStatePresenter = useProblemStatePresenter()
    const { displayLanguage } = useConfigStatePresenter()
    const problemDescriptionPresenter = useProblemDescriptionPresenter()
    const arenaPresenter = useArenaPresenter()

    const preList = computed(() =>
      problemDescriptionPresenter.problemDescription.value.commentaries.filter(
        (c: { pattern: string }) => c.pattern == 'pre'
      )
    )

    const postList = computed(() =>
      problemDescriptionPresenter.problemDescription.value.commentaries.filter(
        (c: { pattern: string }) => c.pattern == 'post'
      )
    )

    const setPrevArenaProblem = () => {
      const prevIndex = arenaPresenter.arenaProblemIndex.value - 1
      if (prevIndex < 0) return
      arenaPresenter.setArenaProblem(
        arenaPresenter.arenaProblemElementList.value[prevIndex].problemId,
        arenaPresenter.arenaId.value,
        arenaPresenter.arenaProblemElementList.value[prevIndex].id,
        prevIndex
      )
    }

    const get_topicId = (problemId: string) => {
      return problemId.slice(0, 5)
    }

    return {
      isLogin,
      displayLanguage,
      leftSideFrame,
      setLeftSideFrame,
      setPrevArenaProblem,
      get_topicId,
      preList,
      postList,
      problemStatePresenter,
      arenaPresenter
    }
  }
})
</script>

<style lang="postcss" scoped>
.hover-menu {
  text-decoration: none;

  &:hover {
    @apply relative;

    &::before {
      content: '';
      right: -12px;
      border-bottom: 8px solid transparent;
      border-top: 8px solid transparent;
      border-right: 8px solid #232a3b;
      @apply block absolute;
    }

    &::after {
      font-family: sans-serif;
      content: attr(data-tooltip);
      left: 130%;
      @apply bg-aoj text-white rounded p-2 block absolute font-bold text-base pointer-events-none z-10;
    }
  }
}

.hover-menu-dark {
  text-decoration: none;

  &:hover {
    @apply relative;

    &::before {
      content: '';
      right: -12px;
      border-bottom: 8px solid transparent;
      border-top: 8px solid transparent;
      border-right: 8px solid white;
      @apply block absolute;
    }

    &::after {
      font-family: sans-serif;
      content: attr(data-tooltip);
      left: 130%;
      @apply bg-black border text-dark-text rounded p-2 block absolute font-bold text-base pointer-events-none z-10;
    }
  }
}

.click-forward {
  &:active {
    @apply text-aoj-click;
  }
  &:focus {
    @apply outline-none;
  }
}

.click-forward-dark {
  &:active {
    @apply text-dark-100;
  }
  &:focus {
    @apply outline-none;
  }
}

.select {
  user-select: none;
}
</style>
